import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import type { CodeoSkill } from '../types';
import { anchoredOffCentre, check, hasStrokeDraw } from '../skillUsage';

export const statBarsAndFillsSkill: CodeoSkill = {
    name: SKILL_NAMES.statBarsAndFills,
    kind: 'codeo',
    description:
        'Pair a number with a graphic that gives it visual weight — a small bar chart, a progress ' +
        'bar or ring filling to a percentage, or a fractional star rating. For stat and metric moments.',
    checkLooksFor:
        'a bar or fill scaled on one axis from an off-centre origin, a stroke drawn to a computed length, or a clipPath inset wipe',
    // One progress fill is as much this skill as a row of bars, so plurality is not required.
    documentUsesSkill: check(
        (script, markup) =>
            (/scaleY|scaleX/.test(script) && anchoredOffCentre(`${markup}${script}`)) ||
            (hasStrokeDraw(script) && /strokeDash(?:offset|array):\s*[\w.]+\s*\*/.test(script)) ||
            /clipPath:\s*['"`]inset\(/.test(script),
    ),
    instructions: `# Stat Bars & Fills

A number alone states a fact; a number with a graphic *shows* it. These are the three primitives that give a stat visual weight: growth bars, a progress fill (bar or ring), and a fractional star rating. Pair the graphic with \`${SKILL_NAMES.counterTickUp}\` (the number) so the two land together as one beat.

Pick ONE layout archetype and hold it across every stat in this graphic: **single-focus** (one centered frame, the number is the hero, a ring or bar sits under it) or **split-frame** (big number left, graphic right). Mixing archetypes between stats reads as inconsistent.

Animate with transforms + \`clipPath\` only — never tween \`width\`/\`height\`. Scale and clip run on the compositor and stay crisp; size tweens reflow every frame and jitter under H.264.

## Markup

\`\`\`html
<!-- Growth bars: the real figures are declared on the container, and each shape drawn from one of
     them carries data-l5-bar, in the order the figures are declared -->
<div id="bars" data-l5-values="[1.2, 1.9, 2.4, 2.8]" style="position: absolute; left: 1040px; top: 360px; display: flex; align-items: flex-end; gap: 14px; height: 280px;">
  <div class="bar" data-l5-bar style="width: 48px; background: var(--l5-color-2, #d7d7e2);"></div>
  <div class="bar" data-l5-bar style="width: 48px; background: var(--l5-color-2, #d7d7e2);"></div>
  <div class="bar" data-l5-bar style="width: 48px; background: var(--l5-color-2, #d7d7e2);"></div>
  <div class="bar" data-l5-bar style="width: 48px; background: var(--l5-color-1, #7a6dff);"></div>
</div>

<!-- Progress ring: an SVG circle drawn to a fraction (see ${SKILL_NAMES.svgDrawOn} for the measure-then-offset mechanics) -->
<svg id="ring-svg" width="220" height="220" viewBox="0 0 220 220" style="position: absolute; left: 1120px; top: 380px;">
  <circle cx="110" cy="110" r="96" fill="none" stroke="var(--l5-color-2, #d7d7e2)" stroke-width="14"/>
  <circle id="ring" cx="110" cy="110" r="96" fill="none" stroke="var(--l5-color-1, #7a6dff)" stroke-width="14"
          stroke-linecap="round" style="transform: rotate(-90deg); transform-origin: 110px 110px;"/>
</svg>
\`\`\`

The ring's static \`rotate(-90deg)\` (so the fill starts at 12 o'clock) is a fixed offset animated only via a \`to\` on the (non-transform) \`strokeDashoffset\` channel — safe to leave in CSS.

## Timeline code

\`\`\`js
const tl = gsap.timeline({ paused: true });

// Growth bars — every height derived from the declared figures, never typed
const barBox = document.querySelector('#bars');
const barValues = JSON.parse(barBox.dataset.l5Values);
const BARS_H = 280;
const barMax = Math.max(...barValues);
barBox.querySelectorAll('.bar').forEach((bar, i) => {
  bar.style.height = (barValues[i] / barMax) * BARS_H + 'px';
});

// …then grow up from the baseline with a stagger, accent bar last
gsap.set('#bars .bar', { transformOrigin: 'bottom center' });
tl.fromTo('#bars .bar', { scaleY: 0 }, { scaleY: 1, duration: 0.7, ease: 'power3.out', stagger: 0.08 }, 0.2);

// Progress fill (bar form) — scaleX from a left origin; the fill div needs width:100% in CSS or scaleX of a 0px box stays invisible
const PCT = 0.92;
gsap.set('#progress-fill', { transformOrigin: 'left center' });
tl.fromTo('#progress-fill', { scaleX: 0 }, { scaleX: PCT, duration: 1.0, ease: 'power2.out' }, 0.2);

// Progress ring — measured stroke draw to the fraction
const ring = document.querySelector('#ring');
const ringLen = Math.ceil(ring.getTotalLength()) + 2;
gsap.set(ring, { strokeDasharray: ringLen, strokeDashoffset: ringLen + 1 });
tl.to(ring, { strokeDashoffset: ringLen * (1 - PCT), duration: 1.1, ease: 'power2.out' }, 0.2);

window.__timelines = { main: tl };
\`\`\`

Star rating (fractional): two star rows stacked, a gold layer over a gray layer, revealed left-to-right with a clip wipe — \`tl.to('#gold-stars', { clipPath: 'inset(0 8% 0 0)', duration: 1.0 }, 0.2)\` for 4.6/5 (8% = the missing 0.4 star).

## Adapt

- **Bar count**: 4–6 reads as "a trend" without clutter; the last bar is the current/accent value.
- **Fill duration**: 0.8–1.2s, matched to the paired count-up so number and graphic peak together — share the start time AND the ease.
- **Accent hue**: exactly one. Bars/fill/stars all use the accent token; the rest is muted (a neutral surface token).
- **Stagger**: 0.06–0.1s on bars; larger feels sluggish, 0 loses the build.
- Pair with \`${SKILL_NAMES.counterTickUp}\` for the number and \`${SKILL_NAMES.svgDrawOn}\` for the ring-draw mechanics.

## Pitfalls

- **Never tween \`width\`/\`height\`** — use \`scaleY\`/\`scaleX\`/\`clipPath\`. Set the final size in CSS and scale from 0.
- \`transform-origin\` must be \`bottom\` (bars grow up) or \`left\` (fills grow right) — the default center origin scales from the middle and looks wrong. Set it with \`gsap.set(..., { transformOrigin })\`, which touches no transform channel.
- A progress-fill div needs \`width: 100%\` in CSS; an absolutely-positioned fill with no width is 0px wide and \`scaleX\` of 0px is still 0 — it renders invisible.
- Measure the ring with \`getTotalLength()\`; a hard-coded circumference breaks the instant the radius changes. Keep the \`+ 2\` on the dash and the \`+ 1\` on the offset, which park the whole path inside the pattern's gap. A dash equal to the measured length puts the path's ends on the dash/gap boundary, where sub-unit slop paints a fraction of a unit that round caps blow up into a full-width dot.
- The bars/ring animate via \`fromTo\` scale (bars) and a \`to\` on \`strokeDashoffset\` (ring) — no CSS \`transform\` on the bars, and the ring's only CSS transform (\`rotate(-90deg)\`) is never itself tweened, so there's no transform conflict.`,
};
